import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

import { ref, computed, nextTick, watch } from 'vue';
import {
  BaseDialog,
  BaseTextContent,
  BaseButton,
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useInputColumn,
  useDatePickerColumn,
  useSelectColumn,
  XrkMessage
} from 'xrk-components';
import { formatter } from '@/utils';
import {
  individualNotInvoicedPageList,
  queryInvoiceOrder
} from '@V/invoice/src/data-source/apis';
import { SETTLEMENT_STATUS_OPTIONS } from '@V/invoice/src/constants/index';


export default /*#__PURE__*/_defineComponent({
  __name: 'invoice-select-data-dialog',
  props: {
    globalPreSelectedIds: { default: () => [] },
    currentPreSelectedIds: { default: () => [] },
    individualId: {},
    remove: {},
    invoiceApplyId: {},
    invoiceBusinessType: {},
    type: {}
  },
  emits: ["confirm"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;

const isAdd = props.type === 1;
const ajaxFn = isAdd ? individualNotInvoicedPageList : queryInvoiceOrder;

const xrkTableRef = ref();
const ajaxDone = ref(false);

const selectionCount = computed(() => {
  return xrkTableRef.value?.getSelectionRows?.()?.length || 0;
});

/** 传给 API 排除的 ID 集合：全局已选 减去 本组已选（本组自己的数据仍需出现在列表中） */
const excludeIds = computed(() => {
  const set = new Set(props.globalPreSelectedIds);
  props.currentPreSelectedIds.forEach(id => set.delete(id));
  return [...set];
});

const preSelectRows = () => {
  nextTick(() => {
    if (!xrkTableRef.value) return;
    const table = xrkTableRef.value;
    const data = table.getTableData?.()?.value || table.getTableData?.() || [];
    const rows = Array.isArray(data) ? data : [];
    rows.forEach(row => {
      if (props.currentPreSelectedIds.includes(row.settlementUserTaskId)) {
        table.addSelectionRow?.(row.settlementUserTaskId, row);
      }
    });
  });
};

const getListAjax = async (params: object) => {
  const resp = await ajaxFn({
    ...params,
    ...(props.individualId && {
      individualId: props.individualId
    }),
    ...(excludeIds.value.length && {
      settlementUserTaskIds: excludeIds.value
    }),
    ...(props.invoiceApplyId && {
      invoiceApplyId: props.invoiceApplyId
    }),
    ...(props.invoiceBusinessType && {
      invoiceBusinessType: props.invoiceBusinessType
    })
  } as any);
  ajaxDone.value = true;
  return resp;
};

watch(
  () => ajaxDone.value,
  v => {
    if (v) {
      nextTick(() => preSelectRows());
    }
  }
);

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    useInputColumn({
      prop: 'settlementNo',
      label: '结算单号',
      config: { placeholder: '请输入结算单号', clearable: true }
    }),
    useInputColumn({
      prop: 'projectName',
      label: '项目名称',
      config: { placeholder: '请输入项目名称', clearable: true }
    }),
    useInputColumn({
      prop: 'industryName',
      label: '工业名称',
      config: { placeholder: '请输入工业名称', clearable: true }
    }),
    useDatePickerColumn({
      prop: 'settlementConfirmTime',
      label: '结算单确认时间',
      config: {
        type: 'daterange',
        startPlaceholder: '开始日期',
        endPlaceholder: '结束日期',
        clearable: true
      },
      listener: {
        onChange: (value, searchObj) => {
          searchObj.settlementConfirmTimeStart = value ? value[0] : '';
          searchObj.settlementConfirmTimeEnd = value ? value[1] : '';
        }
      }
    }),
    useSelectColumn({
      prop: 'settlementStatus',
      label: '结算单状态',
      config: {
        clearable: true,
        placeholder: '请选择',
        options: SETTLEMENT_STATUS_OPTIONS
      }
    })
  ],
  searchBtn: { show: true, text: '搜索' },
  resetBtn: { show: true, text: '重置' }
});

const tableConfig = computed<XrkTableProps['tableConfig']>(() => ({
  tableColumns: [
    isAdd &&
      useTableColumn({
        type: '_selection',
        prop: 'settlementUserTaskId',
        align: 'center',
        fixed: 'left',
        width: 110
      }),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '60px',
      fixed: 'left'
    }),
    useTableColumn({ label: '结算单号', prop: 'settlementNo', width: '180px' }),
    useTableColumn({
      label: '项目名称',
      prop: 'projectName',
      minWidth: '140px'
    }),
    useTableColumn({ label: '工业名称', prop: 'industryName', width: '160px' }),
    useTableColumn({
      label: '服务商名称',
      prop: 'serviceProviderName',
      width: '160px'
    }),
    useTableColumn({
      label: '个体户名称',
      prop: 'individualName',
      width: '150px'
    }),
    useTableColumn({ label: '经营者', prop: 'operator', width: '100px' }),
    useTableColumn({
      label: '手机号',
      prop: 'phone',
      width: '130px',
      formatter: ({ cellValue }) => formatter.maskPhone(cellValue)
    }),
    useTableColumn({
      label: '终端结算金额(元)',
      prop: 'terminalSettlementAmount',
      width: '160px',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    useTableColumn({
      label: '结算单确认时间',
      prop: 'settlementConfirmTime',
      minWidth: '170px',
      formatter: ({ cellValue }) => formatter.date(cellValue)
    }),
    useTableColumn({
      label: '结算单状态',
      prop: 'settlementStatus',
      width: '120px',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(
          cellValue,
          () =>
            SETTLEMENT_STATUS_OPTIONS.find(o => o.value === cellValue)?.label ||
            '-'
        )
    })
  ].filter(Boolean)
}));

const onConfirm = () => {
  const rows = xrkTableRef.value?.getSelectionRows?.() || [];
  if (!rows.length) {
    XrkMessage.warning('请选择开票数据');
    return;
  }
  emits('confirm', rows);
  props.remove?.();
};

const onCancel = () => {
  props.remove?.();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BaseDialog), {
    width: "1200px",
    title: _ctx.type === 1 ? '选择开票数据' : '数据明细',
    "model-value": true,
    "close-on-press-escape": true,
    "close-on-click-modal": false,
    onClose: onCancel
  }, {
    footer: _withCtx(() => [
      isAdd
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(_unref(BaseButton), {
              type: "default",
              onClick: onCancel
            }, {
              default: _withCtx(() => [
                _createTextVNode("取消")
              ]),
              _: 1
            }),
            _createVNode(_unref(BaseButton), {
              type: "primary",
              onClick: onConfirm
            }, {
              default: _withCtx(() => [
                _createTextVNode(" 确认 "),
                (selectionCount.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createTextVNode(" （已选 " + _toDisplayString(selectionCount.value) + "） ", 1)
                    ], 64))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ], 64))
        : (_openBlock(), _createBlock(_unref(BaseButton), {
            key: 1,
            type: "default",
            onClick: onCancel
          }, {
            default: _withCtx(() => [
              _createTextVNode("关闭")
            ]),
            _: 1
          }))
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(BaseTextContent), null, {
        default: _withCtx(() => [
          _createVNode(_unref(XrkTable), {
            ref_key: "xrkTableRef",
            ref: xrkTableRef,
            requestNow: true,
            getListAjax: getListAjax,
            searchConfig: searchConfig.value,
            tableConfig: tableConfig.value
          }, null, 8, ["searchConfig", "tableConfig"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})